import { logger } from '@inboundx/shared/platform';
import { LogOut, SlidersHorizontal, User } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';

export function AuthButton() {
    const { user, signOut } = useAuth();
    const { showAdvanced, setShowAdvanced } = useBackofficeAuth();

    if (!user) {
        return null;
    }

    const initials = user.email
        ?.split('@')[0]
        .split('.')
        .map((n) => n[0]?.toUpperCase())
        .join('')
        .slice(0, 2);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <Avatar className="h-8 w-8">
                        <AvatarImage
                            src={user.user_metadata?.avatar_url}
                            alt={user.email || 'User'}
                        />
                        <AvatarFallback className="bg-foreground text-xs font-semibold text-background">
                            {initials || <User className="h-4 w-4" />}
                        </AvatarFallback>
                    </Avatar>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64" align="end" forceMount>
                <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                        {user.user_metadata?.full_name && (
                            <p className="font-medium">{user.user_metadata.full_name}</p>
                        )}
                        <p className="w-[200px] truncate text-sm text-muted-foreground">
                            {user.email}
                        </p>
                    </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    role="menuitemcheckbox"
                    aria-checked={showAdvanced}
                    // Keep the menu open so the switch visibly flips.
                    onSelect={(event) => {
                        event.preventDefault();
                        setShowAdvanced(!showAdvanced).catch((err) => {
                            logger.error('Failed to save advanced mode:', err);
                            toast.error('Could not save advanced settings');
                        });
                    }}
                    className="cursor-pointer"
                >
                    <SlidersHorizontal className="mr-2 h-4 w-4" />
                    Show advanced settings
                    <Switch
                        checked={showAdvanced}
                        tabIndex={-1}
                        aria-hidden
                        className="pointer-events-none ml-auto"
                    />
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="cursor-pointer text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
